<template>
  <div v-if="loading" class="flex justify-center items-center p-20">
    <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 opacity-75"></div>
        </div>

  <div v-else-if="error" class="trezo-card bg-white dark:bg-[#0c1427] p-[20px] md:p-[25px] rounded-md mb-[25px]">
    <div class="text-center p-10">
      <div class="text-danger-500 text-3xl mb-3">
        <i class="ri-error-warning-line"></i>
      </div>
      <h3 class="text-xl font-medium mb-2 text-danger-500">Error Loading Properties</h3>
      <p class="text-gray-500 dark:text-gray-400">{{ error }}</p>
    </div>
        </div>

  <div v-else-if="properties.length === 0" class="trezo-card bg-white dark:bg-[#0c1427] p-[20px] md:p-[25px] rounded-md mb-[25px]">
    <div class="text-center p-10">
      <div class="text-gray-400 text-3xl mb-3">
        <i class="ri-building-line"></i>
      </div>
      <h3 class="text-xl font-medium mb-2 text-gray-700 dark:text-gray-300">No Properties Found</h3>
      <p class="text-gray-500 dark:text-gray-400">Your organization doesn't have any properties yet.</p>
        <RouterLink
        to="/real-estate/add-property" 
        class="inline-block mt-4 font-medium px-5 py-2 rounded-md bg-primary-500 text-white hover:bg-primary-400 transition-colors"
      >
        Add Your First Property
          </RouterLink>
        </div>
      </div>

  <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[25px] mb-[25px]">
    <div
      v-for="property in paginatedProperties"
      :key="property.id"
      class="trezo-card bg-white dark:bg-[#0c1427] p-[20px] md:p-[25px] rounded-md"
    >
      <div
        class="trezo-card-header mb-[20px] flex items-center justify-between"
      >
        <div class="trezo-card-title">
          <h5 class="mb-0">{{ property.city || 'No City' }}</h5>
        </div>
        <div class="trezo-card-subtitle">
          <DropdownBtn />
        </div>
      </div>
      <div class="trezo-card-content">
        <RouterLink
          :to="`/real-estate/property-details?id=${property.id}`"
          class="block rounded-[5px]"
        >
          <img
            :src="property.main_image_url || placeholderImage"
            class="inline-block rounded-[5px] w-full h-48 object-cover"
            alt="property-image"
            @error="handleImageError"
          />
        </RouterLink>
        <div class="flex items-center justify-between mb-[9px] mt-[20px]">
          <h3 class="text-lg mb-0 text-orange-500">{{ formatPrice(property.price) }}</h3>
          <span
            class="inline-block text-xs rounded-[4px] py-px px-[9px]"
            :class="property.rent_or_sale === 'rent' ? 'bg-secondary-100 text-secondary-600' : 'bg-success-100 text-success-600'"
          >
            {{ property.rent_or_sale === 'rent' ? 'Rent' : 'Sale' }}
          </span>
        </div>
        <h4 v-if="property.title" class="text-base font-medium mb-2 text-gray-800 dark:text-white truncate">
          {{ property.title }}
        </h4>
        <span class="block relative pt-px ltr:pl-[22px] rtl:pr-[22px]">
          <i
            class="material-symbols-outlined text-primary-500 absolute ltr:-left-[2px] rtl:-right-[2px] top-1/2 -translate-y-1/2 !text-[19px]"
          >
            location_on
          </i>
          {{ property.address || 'No Address' }}
        </span>
        <ul
          class="mt-[17px] py-[10px] border-y border-primary-50 dark:border-[#172036]"
        >
          <li
            class="relative inline-block relative ltr:pl-[24px] rtl:pr-[24px] ltr:mr-[20px] rtl:ml-[20px] ltr:last:mr-0 rtl:last:ml-0"
          >
            <i
              class="material-symbols-outlined absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 text-primary-500 !text-[18px]"
            >
              bed
            </i>
            {{ property.bedroom || '0' }}Bed
          </li>
          <li
            class="relative inline-block relative ltr:pl-[24px] rtl:pr-[24px] ltr:mr-[20px] rtl:ml-[20px] ltr:last:mr-0 rtl:last:ml-0"
          >
            <i
              class="material-symbols-outlined absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 text-primary-500 !text-[18px]"
            >
              bathtub
            </i>
            {{ property.bathroom || '0' }}Bath
          </li>
          <li
            class="relative inline-block relative ltr:pl-[24px] rtl:pr-[24px] ltr:mr-[20px] rtl:ml-[20px] ltr:last:mr-0 rtl:last:ml-0"
          >
            <i
              class="material-symbols-outlined absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 text-primary-500 !text-[18px]"
            >
              square_foot
            </i>
            {{ property.size || '0' }}sqft
          </li>
        </ul>
        <div class="mt-[20px] flex items-center justify-between">
          <div class="flex items-center gap-[12px]">
            <div class="w-[40px] h-[40px] rounded-full bg-gray-200 flex items-center justify-center">
              <i class="material-symbols-outlined text-gray-500">person</i>
            </div>
            <div>
              <span
                class="sm:text-[15px] md:text-md block font-semibold text-black dark:text-white"
              >
                {{ property.assignedUser?.display_name || 'Unassigned' }}
              </span>
              <span class="block">{{ property.status || 'Status Unknown' }}</span>
            </div>
          </div>
          <RouterLink
            :to="`/real-estate/property-details?id=${property.id}`"
            class="inline-block font-medium sm:text-[15px] md:text-md text-primary-500 transition-all hover:text-primary-400 hover:underline"
          >
            View More
          </RouterLink>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="properties.length > 0"
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div class="trezo-card-content">
      <Pagination 
        :items="itemsPerPage" 
        :total="properties.length" 
        class="pt-[0] md:pt-[0]"
        @page-change="handlePageChange" 
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import DropdownBtn from "./DropdownBtn.vue";
import Pagination from "@/components/Common/Pagination.vue";
import { supabase } from "@/utils/supabase";
import placeholderImage from '@/assets/images/properties/property-placeholder.jpg';
import { formatCurrency, getUserCurrency } from "@/utils/currency";

interface Property {
  id: string;
  org_id: string;
  status: string;
  price: number;
  rent_or_sale: string;
  description: string;
  features: string;
  neighborhood_info: string;
  country: string;
  city: string;
  address: string;
  bedroom: number;
  bathroom: number;
  size: number;
  main_image_url: string;
  created_by: string;
  assigned_to: string;
  assignedUser?: {
    display_name: string;
  };
  title: string;
}

export default defineComponent({
  name: "PropertyList",
  components: {
    DropdownBtn,
    Pagination,
  },
  setup() {
    const properties = ref<Property[]>([]);
    const loading = ref(true);
    const error = ref('');
    const currentPage = ref(1);
    const itemsPerPage = ref(9);
    const currencyCode = ref('ILS'); // ברירת מחדל

    const paginatedProperties = computed(() => {
      const startIndex = (currentPage.value - 1) * itemsPerPage.value;
      const endIndex = startIndex + itemsPerPage.value;
      return properties.value.slice(startIndex, endIndex);
    });

    const formatPrice = (price: number | null) => {
      if (!price) return formatCurrency(0, currencyCode.value);
      return formatCurrency(price, currencyCode.value);
    };

    const handleImageError = (event: Event) => {
      const target = event.target as HTMLImageElement;
      target.src = placeholderImage;
    };

    const handlePageChange = (page: number) => {
      currentPage.value = page;
    };

    const getOrgId = async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user) {
          throw new Error('User not authenticated');
        }

        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', userData.user.id)
          .single();

        if (profileError) {
          throw new Error(profileError.message);
        }

        if (!profileData) {
          throw new Error('Profile data not found');
        }

        return profileData.org_id;
      } catch (err) {
        console.error('Error getting org ID:', err);
        return null;
      }
    };

    // קבלת המטבע של הארגון
    const getCurrency = async () => {
      try {
        currencyCode.value = await getUserCurrency();
      } catch (error) {
        console.error('Error fetching currency:', error);
      }
    };

    const loadProperties = async () => {
      try {
        loading.value = true;
        error.value = '';

        const orgId = await getOrgId();
        if (!orgId) {
          throw new Error('Could not determine your organization');
        }

        // First, fetch properties
        const { data: propertyData, error: fetchError } = await supabase
          .from('property')
          .select('*')
          .eq('org_id', orgId)
          .order('created_at', { ascending: false });

        if (fetchError) {
          throw new Error(fetchError.message);
        }

        if (!propertyData || propertyData.length === 0) {
          properties.value = [];
          return;
        }

        // Get all unique assigned_to IDs
        const assignedUserIds = propertyData
          .map(property => property.assigned_to)
          .filter(id => id !== null && id !== undefined);

        const uniqueUserIds = [...new Set(assignedUserIds)];

        if (uniqueUserIds.length === 0) {
          // No assigned users, just return properties
          properties.value = propertyData as unknown as Property[];
          return;
        }

        // Fetch user profiles
        const { data: userProfileData, error: userProfileError } = await supabase
          .from('user_profiles')
          .select('user_id, display_name')
          .in('user_id', uniqueUserIds);

        if (userProfileError) {
          throw new Error(userProfileError.message);
        }

        // Create a map of user_id to display_name
        const userMap = new Map();
        if (userProfileData) {
          userProfileData.forEach(profile => {
            userMap.set(profile.user_id, profile.display_name);
          });
        }

        // Combine the data
        properties.value = propertyData.map(property => ({
          ...property,
          assignedUser: property.assigned_to 
            ? { display_name: userMap.get(property.assigned_to) || 'Unknown User' } 
            : null
        })) as Property[];
        
      } catch (err) {
        console.error('Error loading properties:', err);
        error.value = err instanceof Error ? err.message : 'An unknown error occurred';
      } finally {
        loading.value = false;
      }
    };

    onMounted(async () => {
      await getCurrency();
      loadProperties();
    });

    return {
      properties,
      loading,
      error,
      currentPage,
      itemsPerPage,
      paginatedProperties,
      formatPrice,
      handleImageError,
      handlePageChange
    };
  }
});
</script>

<style lang="scss" scoped>
@media (min-width: 768px) {
  .md\:pt-\[0\] {
    padding-top: 0 !important;
  }
}
.pt-\[0\] {
  padding-top: 0 !important;
}
</style>
